import { filterVisibleComposition } from "./visibility";
import { isMeaningfullyAnswered } from "./answerResolution";

/**
 * Stage machine_key → shell final step (Final*.vue, not in master layout).
 * Only stages with a final SFC; dynamic stages need no entry.
 */
const STAGE_FINAL_STEP = {
  "build-team": "final-build-team",
  "build-company-profile": "final-build-company-profile",
  "describe-organizational-landscape": "final-organizational-landscape",
  "describe-cybersecurity-capabilities": "cybersecurity-good-work",
  "build-risk-profile": "final-build-risk-profile",
};

const DEFAULT_STAGE_FINAL_STEP = "final-stage-complete";

const SHELL_GATE_STEPS = new Set(["add-expertise-to-team", "quick-start-final-completion"]);

const ALL_SHELL_FINAL_STEPS = new Set([
  ...Object.values(STAGE_FINAL_STEP),
  DEFAULT_STAGE_FINAL_STEP,
]);

function stageNavKey(stage) {
  return stage?.machineKey || stage?.entityId || stage?.key || "";
}

function screenNavKey(screen) {
  return screen?.machineKey || screen?.entityId || screen?.key || "";
}

function screenMatches(screen, screenKeyOrId) {
  const needle = screenKeyOrId == null ? "" : String(screenKeyOrId);
  return (
    String(screen.entityId || "") === needle ||
    String(screen.machineKey || "") === needle ||
    String(screen.key || "") === needle
  );
}

/** Shell gate/final step (composed screens win when present in visibleScreens). */
export function isInterstitialStageStep(stepKey, visibleScreens) {
  if (!stepKey) return false;
  if ((visibleScreens || []).some(({ screen }) => screen && screenMatches(screen, stepKey))) {
    return false;
  }
  return SHELL_GATE_STEPS.has(stepKey) || ALL_SHELL_FINAL_STEPS.has(stepKey);
}

function isVisibleQuestionAnswered(question, answersById) {
  const map = answersById || {};
  if (question.canonicalQuestionId && Object.hasOwn(map, question.canonicalQuestionId)) {
    return isMeaningfullyAnswered(map[question.canonicalQuestionId]);
  }
  if (question.machineKey && Object.hasOwn(map, question.machineKey)) {
    return isMeaningfullyAnswered(map[question.machineKey]);
  }
  return false;
}

function progressPercent(questions, answersById) {
  if (!questions.length) return 0;
  let answered = 0;
  for (const question of questions) {
    if (isVisibleQuestionAnswered(question, answersById)) answered += 1;
  }
  return Math.round((answered / questions.length) * 100);
}

/** Wizard header stages from visible composition. */
export function visibleWizardStages(visibleScreens) {
  const stages = [];
  const seen = new Set();
  for (const { stage } of visibleScreens || []) {
    const key = stageNavKey(stage);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    stages.push({
      key,
      title: stage.title || key,
      machineKey: stage.machineKey || "",
      entityId: stage.entityId || null,
    });
  }
  return stages;
}

/** Wizard spine: visible screens + known shell finals. */
export function buildWizardScreensList(visibleScreens, { trailingStep } = {}) {
  const list = {};
  for (const { stage, screen } of visibleScreens || []) {
    const stageKey = stageNavKey(stage);
    const screenKey = screenNavKey(screen);
    if (!stageKey || !screenKey) continue;
    if (!list[stageKey]) list[stageKey] = [];
    list[stageKey].push(screenKey);
  }

  for (const stageKey of Object.keys(list)) {
    list[stageKey].push(STAGE_FINAL_STEP[stageKey] || DEFAULT_STAGE_FINAL_STEP);
  }

  if (trailingStep) {
    const stageKeys = Object.keys(list);
    if (stageKeys.length) list[stageKeys.at(-1)].push(trailingStep);
  }

  return list;
}

/** True when on the first wizard step. */
export function isWizardStartStep({ screensList, currentStage, eventType }) {
  const stageKeys = Object.keys(screensList || {});
  if (!stageKeys.length) return true;
  const firstStage = stageKeys[0];
  const firstStep = (screensList[firstStage] || [])[0];
  if (firstStep == null) return true;
  return currentStage === firstStage && eventType === firstStep;
}

/** First visible screen for a stage. */
export function firstVisibleStepForStage(visibleScreens, stageKey) {
  for (const { stage, screen } of visibleScreens || []) {
    if (!stage || !screen) continue;
    const matches =
      stage.machineKey === stageKey ||
      stage.entityId === stageKey ||
      stage.key === stageKey;
    if (!matches) continue;
    return {
      stage: stageNavKey(stage),
      step: screenNavKey(screen),
    };
  }
  return null;
}

/** Hub stage cards from composition. */
export function hubStagesFromComposition(visibleScreens, answersById = {}, knownSteps = []) {
  if (!visibleScreens?.length) {
    return [];
  }

  return visibleWizardStages(visibleScreens).map((stage) => {
    const known = (knownSteps || []).find(
      (s) => s.key === stage.key || s.key === stage.machineKey || s.key === stage.entityId
    );
    const screens = visibleScreens.filter(
      ({ stage: s }) =>
        s &&
        (s.machineKey === stage.key || s.entityId === stage.key || s.key === stage.key)
    );
    const details = screens.map(({ screen }) => {
      const key = screenNavKey(screen);
      const knownDetail = known?.details?.find((d) => d.key === key);
      return {
        key,
        title: knownDetail?.title || screen.title || key,
        shortDescription: knownDetail?.shortDescription || "",
        description: knownDetail?.description || "",
        status: isScreenFullyAnswered(screen, answersById) ? 1 : 0,
        hidden: false,
      };
    });
    return {
      key: stage.key,
      title: stage.title || known?.title || stage.key,
      description: known?.description || "",
      details,
      visible: known?.visible || false,
      loading: false,
      disabled: known?.disabled,
    };
  });
}

/** Sync known-step status from composition answers (match by screen key). */
export function syncKnownStepsProgressFromComposition(knownSteps, visibleScreens, answersById) {
  if (!knownSteps?.length || !visibleScreens?.length) return;

  for (const step of knownSteps) {
    if (!step?.details?.length) continue;
    for (const detail of step.details) {
      const match = visibleScreens.find(
        ({ screen }) => screen && screenNavKey(screen) === detail.key
      );
      if (!match) continue;
      detail.status = isScreenFullyAnswered(match.screen, answersById) ? 1 : 0;
      detail.hidden = false;
    }
  }

  return knownSteps;
}

/** First non-final step from a curated screensList (Coverage spine). */
export function firstStepFromScreensList(screensList, stageKey) {
  const steps = screensList?.[stageKey] || [];
  if (!steps.length) return null;
  const step = steps.find((s) => s && !ALL_SHELL_FINAL_STEPS.has(s)) || steps[0];
  return step ? { stage: stageKey, step } : null;
}

/** First screen of the adjacent stage. */
export function adjacentStageEntry({ visibleScreens, currentStageKey, delta = 1 }) {
  const stages = visibleWizardStages(visibleScreens);
  const idx = stages.findIndex(
    (s) =>
      s.key === currentStageKey ||
      s.machineKey === currentStageKey ||
      s.entityId === currentStageKey
  );
  if (idx < 0) return null;
  const target = stages[idx + delta];
  if (!target) return null;
  return firstVisibleStepForStage(visibleScreens, target.key);
}

export function flattenVisibleScreens(composition, answersById) {
  const tree = filterVisibleComposition(composition, answersById || {});
  const screens = [];
  for (const stage of tree.stages || []) {
    for (const screen of stage.screens || []) {
      screens.push({ stage, screen });
    }
  }
  return screens;
}

function adjacentScreen(composition, answersById, screenKeyOrId, delta) {
  const screens = flattenVisibleScreens(composition, answersById);
  const index = screens.findIndex(({ screen }) => screenMatches(screen, screenKeyOrId));
  if (index < 0) return null;
  return screens[index + delta] || null;
}

export function nextScreen(composition, answersById, screenKeyOrId) {
  return adjacentScreen(composition, answersById, screenKeyOrId, 1);
}

export function prevScreen(composition, answersById, screenKeyOrId) {
  return adjacentScreen(composition, answersById, screenKeyOrId, -1);
}

export function stageProgressPercent(stage, answersById) {
  const questions = [];
  for (const screen of stage.screens || []) {
    questions.push(...(screen.questions || []));
  }
  return progressPercent(questions, answersById);
}

export function overallProgressPercent(composition, answersById) {
  const tree = filterVisibleComposition(composition, answersById || {});
  const questions = [];
  for (const stage of tree.stages || []) {
    for (const screen of stage.screens || []) {
      questions.push(...screen.questions);
    }
  }
  return progressPercent(questions, answersById);
}

function resolveSpinePosition(screensList, stageKeys, currentStage, eventType) {
  let stageIdx = stageKeys.indexOf(currentStage);
  let stepIdx = stageIdx >= 0 ? (screensList[currentStage] || []).indexOf(eventType) : -1;

  // Stage/step URL may not match spine keys — resolve by step.
  if (stageIdx >= 0 && stepIdx >= 0) {
    return { stageIdx, stepIdx };
  }

  for (let s = 0; s < stageKeys.length; s++) {
    const idx = (screensList[stageKeys[s]] || []).indexOf(eventType);
    if (idx < 0) continue;
    return { stageIdx: s, stepIdx: idx };
  }

  return stageIdx < 0 ? null : { stageIdx, stepIdx };
}

function isAllowedSchemaStep(step, visibleScreens) {
  return (
    isInterstitialStageStep(step, visibleScreens) ||
    (visibleScreens || []).some(({ screen }) => screenMatches(screen, step))
  );
}

function findNextAllowedStep(screensList, stageKeys, stageIdx, stepIdx, visibleScreens) {
  for (let s = stageIdx; s < stageKeys.length; s++) {
    const stageKey = stageKeys[s];
    const steps = screensList[stageKey] || [];
    const start = s === stageIdx ? stepIdx + 1 : 0;
    for (let i = start; i < steps.length; i++) {
      if (isAllowedSchemaStep(steps[i], visibleScreens)) {
        return { stage: stageKey, step: steps[i] };
      }
    }
  }
  return null;
}

function findPrevAllowedStep(screensList, stageKeys, stageIdx, stepIdx, visibleScreens) {
  for (let s = stageIdx; s >= 0; s--) {
    const stageKey = stageKeys[s];
    const steps = screensList[stageKey] || [];
    const start = s === stageIdx ? stepIdx - 1 : steps.length - 1;
    for (let i = start; i >= 0; i--) {
      if (isAllowedSchemaStep(steps[i], visibleScreens)) {
        return { stage: stageKey, step: steps[i] };
      }
    }
  }
  return null;
}

export function findAdjacentSchemaAwareStep({
  screensList,
  currentStage,
  eventType,
  delta,
  visibleScreens,
}) {
  const stageKeys = Object.keys(screensList || {});
  const position = resolveSpinePosition(screensList, stageKeys, currentStage, eventType);
  if (!position) return null;

  const { stageIdx, stepIdx } = position;
  if (delta > 0) {
    return findNextAllowedStep(screensList, stageKeys, stageIdx, stepIdx, visibleScreens);
  }
  return findPrevAllowedStep(screensList, stageKeys, stageIdx, stepIdx, visibleScreens);
}

/** Stage bar % by screen position. */
export function stageWizardProgressPercent({ visibleScreens, stageKey, eventType }) {
  if (isInterstitialStageStep(eventType)) return 100;
  const stageScreens = (visibleScreens || []).filter(
    ({ stage }) =>
      stage &&
      (stage.machineKey === stageKey ||
        stage.entityId === stageKey ||
        stage.key === stageKey)
  );
  if (!stageScreens.length) return 0;
  let idx = stageScreens.findIndex(({ screen }) => screenMatches(screen, eventType));
  if (idx < 0) idx = 0;
  return Math.round(((idx + 1) / stageScreens.length) * 100);
}

export function isScreenFullyAnswered(screen, answersById) {
  const questions = screen?.questions || [];
  if (!questions.length) return true;
  return questions.every((question) => isVisibleQuestionAnswered(question, answersById));
}

/** Incomplete screens for a stage (finals pending lists). */
export function pendingStageTasks({ visibleScreens, stageKey, answersById }) {
  const pending = [];
  for (const { stage, screen } of visibleScreens || []) {
    if (!stage || !screen) continue;
    const matches =
      stage.machineKey === stageKey ||
      stage.entityId === stageKey ||
      stage.key === stageKey;
    if (!matches) continue;
    if (isScreenFullyAnswered(screen, answersById)) continue;
    const firstQ = (screen.questions || [])[0];
    pending.push({
      key: screen.machineKey || screen.key,
      title: screen.title || firstQ?.title || screen.machineKey || "",
      shortDescription: firstQ?.displayLabel || firstQ?.title || firstQ?.helpText || "",
      status: 0,
    });
  }
  return pending;
}
